import { create } from 'zustand';
import type { Language } from '@core/i18n';

const DICTATION_LANG_KEY = 'dda-dictation-lang';

/** Null means "follow the interface". A stored value is a deliberate override and
 *  outlives a reload, because someone whose UI is Arabic but whose project names
 *  are English will make the same choice every single time otherwise. */
function readDictationLang(): Language | null {
  try {
    const stored = localStorage.getItem(DICTATION_LANG_KEY);
    return stored === 'en' || stored === 'ar' ? stored : null;
  } catch {
    return null;
  }
}

interface AssistantState {
  /** What is typed in the composer but not yet submitted.
   *
   *  Deliberately NOT in the URL: a half-typed sentence is not a place, and
   *  writing every keystroke to history would make Back walk it letter by
   *  letter. The SUBMITTED query lives in `?q=` instead, so a result list can be
   *  linked and Back leaves it. This only has to survive navigation — someone who
   *  opens a session from the results and comes back should find their sentence
   *  where they left it. */
  draft: string;
  setDraft: (draft: string) => void;
  clearDraft: () => void;
  /** Bumped to ask the composer for focus, by ⌘K or by the rail's search button
   *  from a route where the composer is not mounted yet.
   *
   *  A counter rather than a boolean: two ⌘K presses in a row have to be two
   *  distinct requests, and a boolean that is already `true` produces no state
   *  change and so no second focus. */
  focusNonce: number;
  requestFocus: () => void;
  /** Which language to LISTEN in, independent of the language to read in.
   *
   *  These come apart in practice: the interface is Arabic, but a project is
   *  called "Fawz" or "Trial Conversion", and an Arabic recogniser hands back
   *  Arabic script for an English name — a transcript that can never match. Null
   *  follows the interface, which is the right default and the wrong lock. */
  dictationLang: Language | null;
  setDictationLang: (language: Language) => void;
  /** A sentence the bar handed to the assistant, waiting to be sent as a turn.
   *
   *  The handoff goes through here rather than through a prop because the bar and
   *  the conversation are siblings on the page, and because it must be consumed
   *  exactly once — left set, it would re-send itself on the next render. */
  ask: string | null;
  askAssistant: (text: string) => void;
  clearAsk: () => void;
}

export const useAssistantStore = create<AssistantState>((set) => ({
  draft: '',
  setDraft: (draft) => {
    set({ draft });
  },
  clearDraft: () => {
    set({ draft: '' });
  },
  focusNonce: 0,
  requestFocus: () => {
    set((state) => ({ focusNonce: state.focusNonce + 1 }));
  },
  dictationLang: readDictationLang(),
  setDictationLang: (language) => {
    try {
      localStorage.setItem(DICTATION_LANG_KEY, language);
    } catch {
      /* ignore */
    }
    set({ dictationLang: language });
  },
  ask: null,
  askAssistant: (text) => {
    set({ ask: text });
  },
  clearAsk: () => {
    set({ ask: null });
  },
}));
